import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';

import { monitorViewRaw } from 'pages/Dashboard/monitorView';

import { MONITOR_VIEW } from 'services/consts';

import { Breakpoints } from 'utils/const.styles';
import PATHS from 'utils/paths';

import { analysisChartsConfigRaw, chartsConfig } from '../../pages/Analysis/analysisWidgetsConfig';
import { dashboardChartsConfigRaw } from '../../pages/Dashboard/dashboardConfig';
import { homePageChartsConfigRaw } from '../../pages/Home/RecurringPage/homePageChartsConfig';
import Authorization from './Authorization';
import { WIDGET_VERSION } from './ManageWidgets/widgetsUtils';
import { safeParseLocalStorage } from './WidgetPageContainer';

const WidgetWrapper = ({ widgetId, originalId, children }) => {
	const [widgets, setWidgets] = useState([]);
	const { pathname } = useLocation();
	const isMobile = useMediaQuery(Breakpoints.tablet);
	const axios = useAxiosPrivate();

	const data = useMemo(() => {
		if (pathname.startsWith(PATHS.HOME)) return chartsConfig(homePageChartsConfigRaw);
		if (pathname.startsWith(PATHS.ANALYSIS)) return chartsConfig(analysisChartsConfigRaw);
		if (pathname.startsWith(PATHS.DASHBOARD)) return chartsConfig(dashboardChartsConfigRaw);
		if (pathname.startsWith(PATHS.DASHBOARD_MONITOR_VIEW)) return chartsConfig(monitorViewRaw);
		return {};
	}, [pathname]);

	const getWidgets = useCallback(async () => {
		if (pathname.startsWith(PATHS.DASHBOARD_MONITOR_VIEW) || pathname.startsWith(PATHS.ANALYSIS_MONITOR_VIEW)) {
			const response = await axios.post(MONITOR_VIEW.GET_WIDGETS, { view: pathname });
			return response.data;
		}
		const storedWidgets = localStorage.getItem(`${pathname}${WIDGET_VERSION}`);
		if (storedWidgets) return safeParseLocalStorage(`${pathname}${WIDGET_VERSION}`);
		return data[`${pathname}`];
	}, [data, pathname]);

	useEffect(() => {
		const fetchWidgets = async () => {
			try {
				const result = await getWidgets();
				setWidgets(result || []);
			} catch (error) {
				if (window.AppLogger) {
					window.AppLogger.error('Failed to fetch widgets', error);
				}
			}
		};
		fetchWidgets();
		const handleUpdateWidgetStore = () => {
			fetchWidgets();
		};
		window.addEventListener('updateWidgetStore', handleUpdateWidgetStore);
		return () => {
			window.removeEventListener('updateWidgetStore', handleUpdateWidgetStore);
		};
	}, [getWidgets]);

	const foundWidget = useMemo(
		() =>
			widgets?.find((widget) => (widget.originalId ? widget.originalId === originalId : widget.id === widgetId)),
		[widgets, widgetId, originalId]
	);

	const calculateSpan = useMemo(() => {
		if (isMobile) return 'span 6';
		switch (foundWidget?.size) {
			case 'small':
				return 'span 2';
			case 'medium':
				return 'span 3';
			case 'twoThirds':
				return 'span 4';
			case 'large':
				return 'span 6';
			default:
				return 'span 6';
		}
	}, [isMobile, foundWidget?.size]);

	return (
		<Authorization path={foundWidget?.authorizationPath}>
			{foundWidget?.isVisible && (
				<div style={{ order: foundWidget?.order, gridColumn: calculateSpan }}>{children}</div>
			)}
		</Authorization>
	);
};

export default WidgetWrapper;
